import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AssistantService,
  AssistantThreadListModel,
  AssistantThreadModel,
  ChatHistoryListModel,
  ChatHistoryModel,
} from 'digiteq-ai-portal-client-lib';
import { historyCategoryEnum } from '../../models/history-category.enum';
import { Observable, Subject } from 'rxjs';
import { ChatType } from '../../models/chat-type';
import { StreamChatService } from '../../services/stream-chat.service';
import { ErrorService } from '../../services/error.service';
import { AiAssistantService } from '../../services/ai-assistant.service';
import { ChatViewService } from '../../chat-view.service';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { AppSettingsService } from 'src/app/services/app-settings.service';

interface ChatHistoryListUiModel {
  items: ChatHistoryListModel[];
  label: string;
  isPinned?: boolean;
}

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
})
export class HistoryComponent implements OnInit {
  @Input() history: ChatHistoryListModel[];
  @Input() chatType: ChatType;
  @Input() reloadChatHistory$: Subject<void>;
  @Output() onNewChat = new EventEmitter<void>();
  @Output() onSuccessChatHistoryLoad = new EventEmitter<
    ChatHistoryListModel[]
  >();
  @Output() onChatHistoryItemLoad = new EventEmitter<
    ChatHistoryModel | AssistantThreadModel
  >();

  groupedHistory: ChatHistoryListUiModel[];
  loadingChatHistory: boolean;
  currentChatName: string | undefined;

  readonly confirmationOptions: Confirmation = {
    target: null,
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    rejectButtonStyleClass: 'p-button-text',
  };

  get currentChatId(): string | number | undefined {
    return this.chatViewService.currentChatId;
  }

  set currentChatId(value: string | number | undefined) {
    this.chatViewService.currentChatId = value;
    if (this.isAssistant()) {
      this.aiAssistantService.threadId = value as number | undefined;
    }
  }

  get historyVisible(): boolean {
    return this.settingsService.historyVisible;
  }

  set historyVisible(value: boolean) {
    if (this.settingsService.historyVisible !== value) {
      this.settingsService.historyVisible = value;
    }
  }

  constructor(
    private readonly chatService: StreamChatService,
    private readonly errorService: ErrorService,
    private readonly assistantService: AssistantService,
    private readonly aiAssistantService: AiAssistantService,
    private readonly chatViewService: ChatViewService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly settingsService: AppSettingsService,
  ) {}

  ngOnInit(): void {
    if (this.isAssistant()) {
      this.currentChatId = undefined;
    }
    this.loadChatHistory();
    this.reloadChatHistory$.subscribe(() => {
      this.loadChatHistory();
    });
  }

  loadChatHistory() {
    let chatHistoryListObservable$: Observable<
      ChatHistoryListModel[] | AssistantThreadListModel[]
    >;
    switch (this.chatType) {
      case ChatType.GENERIC:
        chatHistoryListObservable$ = this.chatService.genericHistoryList();
        break;
      case ChatType.COMPANY:
        chatHistoryListObservable$ = this.chatService.companyHistoryList();
        break;
      case ChatType.ASSISTANT:
        chatHistoryListObservable$ =
          this.assistantService.assistantThreadList();
        break;
    }

    this.loadingChatHistory = true;

    chatHistoryListObservable$.subscribe({
      next: (chatHistory) => {
        this.handleHistoryResponse(chatHistory);
        this.loadingChatHistory = false;
      },
      error: (err) => {
        this.loadingChatHistory = false;
        this.errorService.handleError(err, 'Could not get chat history.');
      },
    });
  }

  handleHistoryResponse(chatHistory: any) {
    this.history = chatHistory;
    this.categorizeChatHistory();

    const currentChat = this.history.find(
      (chat) => chat[this.getChatIdProperty()].toString() === this.currentChatId
    );
    this.currentChatName = currentChat?.name;
    this.loadingChatHistory = false;

    this.onSuccessChatHistoryLoad.emit(this.history);
  }

  createNewChat() {
    this.currentChatId = undefined;
    this.onNewChat.emit();
  }

  removeAllHistoryChat() {
    this.confirmationService.confirm({
      ...this.confirmationOptions,
      message: 'Do you want to remove all chat history?',
      accept: () => {
        this.loadingChatHistory = true;
        let removeAllChatHistoryObservable$: Observable<any>;
        switch (this.chatType) {
          case ChatType.GENERIC:
            removeAllChatHistoryObservable$ =
              this.chatService.deleteAllGenericChats();
            break;
          case ChatType.COMPANY:
            removeAllChatHistoryObservable$ =
              this.chatService.deleteAllCompanyChats();
            break;
          case ChatType.ASSISTANT:
            removeAllChatHistoryObservable$ =
              this.assistantService.deleteAllAssistantChats();
            break;
        }

        removeAllChatHistoryObservable$.subscribe({
          next: () => {
            this.history = [];
            this.groupedHistory = [];
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'All chat history was deleted'
            })
            this.loadingChatHistory = false;
            this.createNewChat();
          },
          error: (err) => {
            this.loadingChatHistory = false;
            this.errorService.handleError(
              err,
              'Could not remove all chat history.'
            );
          },
        });
      },
    });
  }

  categorizeChatHistory() {
    const currentDate = new Date();
    const pinnedItems = this.history.filter(h => h.pinned);
    const unpinnedItems = this.history.filter(h => !h.pinned);

    pinnedItems.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    unpinnedItems.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    const groupedUnpinned: ChatHistoryListUiModel[] = [
      { label: historyCategoryEnum.last7days, items: [] },
      { label: historyCategoryEnum.last30days, items: [] },
      { label: historyCategoryEnum.older, items: [] },
    ];

    unpinnedItems.forEach(h => {
      const updatedAt = new Date(h.updatedAt);
      const daysDiff = (currentDate.getTime() - updatedAt.getTime()) / (1000 * 3600 * 24);

      let category = historyCategoryEnum.last7days;

      if (daysDiff > 30) {
        category = historyCategoryEnum.older;
      } else if (daysDiff > 7) {
        category = historyCategoryEnum.last30days;
      }

      const group = groupedUnpinned.find(g => g.label === category);
      group?.items.push(h);
    });

    this.groupedHistory = [];

    if (pinnedItems.length > 0) {
      this.groupedHistory.push({ label: 'Pinned', items: pinnedItems, isPinned: true });
    }

    this.groupedHistory.push(...groupedUnpinned);

  }

  getChatIdProperty() {
    return this.isAssistant() ? 'threadId' : 'chatId';
  }

  isAssistant() {
    return this.chatType === ChatType.ASSISTANT;
  }

  isCurrentChatId(chat: ChatHistoryListModel | AssistantThreadListModel) {
    const idProperty = this.getChatIdProperty();
    return chat[idProperty]?.toString() === this.currentChatId?.toString();
  }

  onChatRename(renameChatData: { id: string; newName: string }) {
    const idProperty = this.getChatIdProperty();
    const historyItem = this.history.find(
      (h) => h[idProperty] === renameChatData.id
    );
    historyItem.name = renameChatData.newName;
  }

  onChatPinned(pinChatData: { id: string, pinned: boolean }) {
    const idProperty = this.getChatIdProperty();
    const historyItem = this.history.find(h => h[idProperty] === pinChatData.id);
    historyItem.pinned = pinChatData.pinned;
    this.categorizeChatHistory();
  }

  onChatHistoryItemDelete(chatId: string) {
    const idProperty = this.getChatIdProperty();
    const index = this.history.findIndex((h) => h[idProperty] === chatId);
    this.history.splice(index, 1);
    this.categorizeChatHistory();
    if (chatId.toString() === this.currentChatId) {
      this.onNewChat.emit();
    }
  }

  handleHistoryItemSelect($event: ChatHistoryModel | AssistantThreadModel) {
    this.currentChatId = $event[this.getChatIdProperty()];
    this.onChatHistoryItemLoad.emit($event);
  }
}
